import React, { useState, FunctionComponent, useEffect, useCallback } from 'react';
import { Presentation } from './presentation';
import { fetchRecommendSkills } from '../../../services/personProfiles';
import { PersonShowModalSkillState } from './types';

type Props = {
  onChangedBody: (name: string, value: any) => void;
  skills: string[];
  target: string;
  onRegisterCommitHandler?: (handler: () => string[]) => void;
};

export const Component: FunctionComponent<Props> = ({
  onChangedBody,
  skills: initialSkills,
  target,
  onRegisterCommitHandler,
}) => {
  const [state, setState] = useState<PersonShowModalSkillState>({
    skills: [] as string[],
    targetSkill: '',
    isSearching: true,
    suggestSkills: [] as { title: string; count: number }[],
    recommendSkills: [] as string[]
  });

  useEffect(() => {
    setState(prev => ({ ...prev, skills: initialSkills }));
  }, [initialSkills]);

  useEffect(() => {
    const request = async () => {
      try {
        const response = await fetchRecommendSkills();
        setState(prev => ({ ...prev, recommendSkills: response.recommendSkills}));
      } catch (error) {
        console.log(error);
      }
    };
    request();
  }, []);

  const handleChangeBody = useCallback((name: string, value: string) => {
    setState(prev => ({
      ...prev,
      [name]: value,
      ...(value === ''
        ? { isSearching: false, suggestSkills: [] }
        : { isSearching: true })
    }));
  }, []);

  const handleClick = useCallback((skillTitle: string) => {
    if (!state.skills.includes(skillTitle)) {
      const updatedSkills = [...state.skills, skillTitle];
      setState(prev => ({
        ...prev,
        skills: updatedSkills,
        suggestSkills: [],
        isSearching: false,
        targetSkill: ''
      }));
      onChangedBody('skills', updatedSkills);
    }
  }, [state.skills, onChangedBody]);

  const handleDelete = useCallback((skillTitle: string) => {
    const updatedSkills = state.skills.filter(skill => skill !== skillTitle);
    setState(prev => ({ ...prev, skills: updatedSkills }));
    onChangedBody('skills', updatedSkills);
  }, [state.skills, onChangedBody]);

  const handleSend = useCallback((value: string) => {
    if (!state.skills.includes(value)) {
      const updatedSkills = [...state.skills, value];
      setState(prev => ({
        ...prev,
        skills: updatedSkills,
        targetSkill: '',
        isSearching: false
      }));
      onChangedBody('skills', updatedSkills);
    }
  }, [state.skills, onChangedBody]);

  const handleClearSuggest = useCallback(() => {
    setState(prev => ({ ...prev, isSearching: false, suggestSkills: [] }));
  }, []);

  const getSkillsForSave = useCallback((): string[] => {
    const value = state.targetSkill.trim();
    if (value && !state.skills.includes(value)) {
      return [...state.skills, value];
    }
    return state.skills;
  }, [state.targetSkill, state.skills]);

  useEffect(() => {
    onRegisterCommitHandler?.(getSkillsForSave);
  }, [getSkillsForSave, onRegisterCommitHandler]);

  return (
    <Presentation
      state={state}
      target={target}
      onChangeBody={handleChangeBody}
      handleClick={handleClick}
      handleDelete={handleDelete}
      handleSend={handleSend}
      handleClearSuggest={handleClearSuggest}
    />
  );
};
